import React, { useState, useRef, useEffect } from "react";
import { FaSearch } from "react-icons/fa";
import { fileIcons } from "../Icons.jsx";

type FileTypeOption = { value: string; label: string; image?: string };

type CustomFileTypeSelectDropdownProps = {
  selectedFileTypes: string[];
  onChange: (selected: string[]) => void;
  isOpen: boolean;
  toggleDropdown: () => void;
  fileTypeOptions?: FileTypeOption[];
  placeholder?: string;

  // ✅ NEW OPTIONAL PROPS (non-breaking)
  enableShowMore?: boolean;
  visibleCount?: number;
};

const defaultFileTypeOptions: FileTypeOption[] = [
  { value: "pdf", label: "PDF" },
  { value: "doc", label: "Doc" },
  { value: "docx", label: "Docx" },
  { value: "xls", label: "Excel(xls)" },
  { value: "xlsx", label: "Excel(xlsx)" },
  { value: "ppt", label: "PowerPoint(ppt)" },
  { value: "pptx", label: "PowerPoint(pptx)" },
  { value: "txt", label: "Text File" },
  { value: "jpg", label: "Image (JPG)" },
  { value: "jpeg", label: "Image (JPEG)" },
  { value: "png", label: "Image (PNG)" },
  { value: "zip", label: "Archive (ZIP)" },
];

const CustomFileTypeSelectDropdown: React.FC<
  CustomFileTypeSelectDropdownProps
> = ({
  selectedFileTypes,
  onChange,
  isOpen,
  toggleDropdown,
  fileTypeOptions = defaultFileTypeOptions,
  placeholder = "Select File Types",

  // ✅ defaults ensure existing behavior remains unchanged
  enableShowMore = false,
  visibleCount = 4,
}) => {
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        toggleDropdown();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, toggleDropdown]);

  // Reset showAll when search changes (better UX, non-breaking)
  useEffect(() => {
    setShowAll(false);
  }, [search]);

  const handleSelect = (fileType: string) => {
    if (fileType === "") {
      onChange([]);
    } else if (selectedFileTypes.includes(fileType)) {
      onChange(selectedFileTypes.filter((type) => type !== fileType));
    } else {
      onChange([...selectedFileTypes, fileType]);
    }
  };

  const filteredOptions = fileTypeOptions.filter((option) =>
    option.label.toLowerCase().includes(search.toLowerCase()),
  );

  const visibleOptions =
    enableShowMore && !showAll
      ? filteredOptions.slice(0, visibleCount)
      : filteredOptions;

  return (
    <div className="w-full mt-0 relative h-full" ref={containerRef}>
      {/* Trigger */}
      <div
        className="border border-dissabled-btn rounded-lg px-2 py-[0.75rem] text-[14px] flex justify-between items-center cursor-pointer"
        onClick={toggleDropdown}
      >
        <span className="ps-2 text-gray-500 flex flex-wrap gap-2">
          {selectedFileTypes.length > 0
            ? fileTypeOptions
                .filter((option) =>
                  selectedFileTypes.includes(option.value),
                )
                .map((option) => (
                  <span
                    key={option.value}
                    className="bg-[#F5F6F5] text-black-900 px-2 py-1 rounded-full flex items-center mr-1 mb-1 gap-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelect(option.value);
                    }}
                  >
                    {option.image ? (
                      <img
                        src={option.image}
                        alt=""
                        className="w-5 h-5 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      fileIcons?.[option.value] &&
                      React.createElement(fileIcons[option.value])
                    )}
                    {option.label}
                    <span className="ml-1 text-black-900 cursor-pointer">
                      &times;
                    </span>
                  </span>
                ))
            : placeholder}
        </span>

        <span>
          <svg
            width="12"
            height="7"
            viewBox="0 0 12 7"
            fill="none"
          >
            <path
              d="M5.65094 4.60344L10.2509 0.00343704L11.3047 1.05719L5.65094 6.71094L-0.00281334 1.05719L1.05094 0.00343704L5.65094 4.60344Z"
              fill="#808080"
            />
          </svg>
        </span>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="relative mt-2 w-full bg-white shadow-md z-10 border border-dissabled-btn rounded-lg px-2 py-3 pt-1 cursor-pointer">
          {/* Search */}
          <div  className="flex items-center p-2 relative">
            <FaSearch className="ps-3 text-gray-400 w-8" />
            <input
              type="text"
              placeholder={placeholder}
              value={search}
              // style={{borderBottom:"1px solid #D9D9D9"}}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full p-1 border-none outline-none ps-4 text-xs text-black"
            />
            <div className="absolute top-8 left-14 right-2 h-[1px] w-[80%] bg-[#D9D9D9]"></div>
          </div>

          {/* Options */}
          <div
            className={`pt-2 mt-2 ${
              enableShowMore ? "" : "max-h-40 overflow-y-auto"
            }`}
          >
            {visibleOptions.map((option) => (
              <div
                key={option.value}
                className="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer text-xs text-black gap-2"
                onClick={() => handleSelect(option.value)}
              >
                <input
                  type="checkbox"
                  checked={selectedFileTypes.includes(option.value)}
                  onChange={() => handleSelect(option.value)}
                  className="mr-2 form-checkbox form-checkbox-min"
                  onClick={(e) => e.stopPropagation()}
                />
                {option.image ? (
                  <img
                    src={option.image}
                    alt=""
                    className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                  />
                ) : (
                  fileIcons?.[option.value] &&
                  React.createElement(fileIcons[option.value])
                )}
                <span className="font-medium">{option.label}</span>
              </div>
            ))}

            {/* Show More / Less */}
            {enableShowMore &&
              filteredOptions.length > visibleCount && (
                <div className="flex justify-start mt-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAll((prev) => !prev);
                    }}
                    className="text-xs text-[#0068FF] hover:underline bg-transparent border-0 text-[0.9rem]"
                  >
                    {showAll ? "Show Less" : `more ${filteredOptions.length - visibleCount}`}
                  </button>
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomFileTypeSelectDropdown;